import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Check } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Logo } from "@/components/AppShell";
import { Busy } from "@/components/ui-bits";

const Shell = ({ children }: any) => (
  <main className="min-h-screen bg-cream">
    <div className="max-w-5xl mx-auto px-5 sm:px-8 h-16 flex items-center"><Logo /></div>
    <div className="max-w-md mx-auto px-5 py-12 animate-rise">{children}</div>
  </main>
);

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");
  const [error, setError] = useState("");
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setBusy(true); setError("");
    try { const { data } = await api.post("/auth/forgot-password", { email, origin_url: window.location.origin }); setDone(data.message); }
    catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Shell>
      <p className="eyebrow">Password reset</p>
      <h1 className="font-serif text-3xl sm:text-4xl mt-2">Forgot your password?</h1>
      {done ? (
        <div className="card-soft p-6 mt-6" data-testid="forgot-success">
          <div className="size-10 rounded-full bg-olive-light text-olive-deep grid place-items-center"><Check className="size-5" /></div>
          <p className="text-sm text-ink-muted mt-4">{done}</p>
          <Link to="/login" className="btn-ghost mt-6" data-testid="forgot-back-login">Back to log in</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4 mt-6" data-testid="forgot-form">
          <p className="text-sm text-ink-muted">Enter the email on your account and we'll send a single-use link that works for 60 minutes.</p>
          <div><label className="label">Email</label><input type="email" className="field" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email-input" /></div>
          {error && <p className="text-sm text-[#687000] bg-clay-light rounded-xl px-4 py-3" data-testid="forgot-error">{error}</p>}
          <button className="btn-ink w-full !py-3.5" disabled={busy} data-testid="forgot-submit-button">{busy ? <Busy label="Sending..." /> : "Send reset link"}</button>
          <p className="text-sm text-ink-muted text-center"><Link to="/login" className="underline text-olive-deep" data-testid="forgot-cancel-link">Never mind, take me back</Link></p>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setError("");
    if (pw !== pw2) { setError("Those passwords don't match."); return; }
    setBusy(true);
    try { await api.post("/auth/reset-password", { token, password: pw }); localStorage.removeItem("bestie_token"); nav("/login?reset=1", { replace: true }); }
    catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Shell>
      <p className="eyebrow">Password reset</p>
      <h1 className="font-serif text-3xl sm:text-4xl mt-2">Choose a new password</h1>
      {!token ? <p className="text-sm text-ink-muted mt-4" data-testid="reset-missing-token">This link is missing its token. <Link to="/forgot-password" className="underline text-olive-deep">Request a new one</Link>.</p> : (
        <form onSubmit={submit} className="space-y-4 mt-6" data-testid="reset-form">
          <div><label className="label">New password</label><input type="password" className="field" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 8 characters" data-testid="reset-password-input" /></div>
          <div><label className="label">Confirm new password</label><input type="password" className="field" required minLength={8} value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="reset-confirm-input" /></div>
          {error && <p className="text-sm text-[#687000] bg-clay-light rounded-xl px-4 py-3" data-testid="reset-error">{error}</p>}
          <button className="btn-ink w-full !py-3.5" disabled={busy} data-testid="reset-submit-button">{busy ? <Busy label="Saving..." /> : "Save new password"}</button>
          <p className="text-xs text-ink-soft text-center">Link expired? <Link to="/forgot-password" className="underline">Request a fresh one</Link>.</p>
        </form>
      )}
    </Shell>
  );
}
